import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydButton from '../../../../commonDesign/Button'
import Switch from '../../../../molecules/Switch'
import { QUICK_DATE_OPTIONS } from './quickDates'

const quickDateOptions = QUICK_DATE_OPTIONS
  .filter(o => o.value !== 'Custom')
  .map(o => ({ value: o.value, label: o.label }))

export const FIRMWIDE_ALLOWED = ['Day', 'WTD', 'MTD', 'QTD', 'L7D']

export { quickDateOptions }

export default function QuickDateChecklist ({ value, onChange, scopeType = 'client' }) {
  const displayedOptions = useMemo(() => {
    if (scopeType === 'firmwide') {
      const allow = new Set(FIRMWIDE_ALLOWED)
      return quickDateOptions.filter(o => allow.has(o.value))
    }
    return quickDateOptions
  }, [scopeType])

  const allDisplayedValues = useMemo(() => displayedOptions.map(o => o.value), [displayedOptions])

  const enabled = useMemo(() => {
    if (value != null) return new Set(value)
    return new Set(allDisplayedValues)
  }, [value, allDisplayedValues])

  const allOn = displayedOptions.length > 0 &&
    displayedOptions.every(o => enabled.has(o.value))

  const handleToggle = useCallback((token) => {
    const next = new Set(enabled)
    if (next.has(token)) {
      next.delete(token)
    } else {
      next.add(token)
    }
    const allSelected = displayedOptions.every(o => next.has(o.value))
    onChange(allSelected ? null : Array.from(next))
  }, [enabled, onChange, displayedOptions])

  const handleToggleAll = useCallback(() => {
    onChange(allOn ? [] : null)
  }, [allOn, onChange])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <SydLabel label='Available Quick Dates' style={{ marginBottom: 0 }} />
        <SydButton variant='ghost' size='xs' onClick={handleToggleAll}>
          {allOn ? 'Disable All' : 'Enable All'}
        </SydButton>
      </div>
      <div style={{
        maxHeight: '240px',
        overflowY: 'auto',
        border: '1px solid var(--color-neutral-200)',
        borderRadius: '6px',
        padding: '4px 0'
      }}
      >
        {displayedOptions.map(opt => (
          <div
            key={opt.value}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '3px 10px',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            <span>{opt.label}</span>
            <Switch
              checked={enabled.has(opt.value)}
              onChange={() => handleToggle(opt.value)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

QuickDateChecklist.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  scopeType: PropTypes.oneOf(['client', 'firmwide'])
}
